import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../styles/Appointment.css';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Service {
  id: number;
  name: string;
  description: string;
  duration: number;
  price: number;
}

const Appointment: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialService = queryParams.get('service');

  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [responseMessage, setResponseMessage] = useState('');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/services`);
        setServices(response.data);
        if (initialService) {
          const serviceFound = response.data.find((s: Service) => s.name === initialService);
          if (serviceFound) {
            setSelectedService(serviceFound);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar serviços:', error);
        setResponseMessage('Erro ao carregar serviços. Tente novamente mais tarde.');
        setStatus('error');
      }
    };
    fetchServices();
  }, [initialService]);

  useEffect(() => {
    const fetchAvailableTimes = async () => {
      if (selectedDate) {
        setStatus('loading');
        setResponseMessage('');
        try {
          const dateString = selectedDate.toISOString().split('T')[0];
          const response = await axios.get(`${API_URL}/api/available-times`, {
            params: { date: dateString, service_id: selectedService?.id }
          });
          setAvailableTimes(response.data);
          setStatus('idle');
        } catch (error) {
          console.error('Erro ao buscar horários disponíveis:', error);
          setResponseMessage('Erro ao carregar horários disponíveis.');
          setStatus('error');
          setAvailableTimes([]);
        }
      }
    };
    fetchAvailableTimes();
  }, [selectedDate, selectedService]);

  const handleServiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const serviceId = parseInt(e.target.value);
    const service = services.find(s => s.id === serviceId);
    setSelectedService(service || null);
    setSelectedDate(null); // Reset date when service changes
    setAvailableTimes([]); // Clear times when service changes
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService || !selectedDate || !formData.phone) {
      setResponseMessage('Por favor, preencha todos os campos obrigatórios.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setResponseMessage('');

    try {
      const appointmentData = {
        service_id: selectedService.id,
        date: selectedDate.toISOString().split('T')[0],
        time: (document.getElementById('appointment-time') as HTMLSelectElement).value, // Get selected time from dropdown
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        notes: formData.notes,
      };
      const response = await axios.post(`${API_URL}/api/appointments`, appointmentData);
      setStatus('success');
      setResponseMessage(response.data.message || 'Agendamento realizado com sucesso!');
      setFormData({
        name: '',
        phone: '',
        email: '',
        notes: '',
      });
      setSelectedService(null);
      setSelectedDate(null);
      setAvailableTimes([]);
    } catch (error: any) {
      setStatus('error');
      setResponseMessage(error.response?.data?.message || 'Erro ao agendar. Tente novamente.');
      console.error('Erro ao agendar:', error);
    }
  };

  return (
    <div className="appointment-page">
      <section className="hero-appointment">
        <div className="container">
          <h1>Agende Sua Consulta</h1>
          <p>Escolha o serviço, a data e o horário que melhor se encaixam na sua rotina.</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="appointment-form-card">
            <h2>Formulário de Agendamento</h2>
            <form onSubmit={handleSubmit} className="appointment-form">
              <div className="form-group">
                <label htmlFor="service">Serviço</label>
                <select
                  id="service"
                  name="service"
                  value={selectedService?.id || ''}
                  onChange={handleServiceChange}
                  required
                >
                  <option value="">Selecione um serviço</option>
		  {Array.isArray(services) ? services.map(service => (
		  <option key={service.id} value={service.id}>
		    {service.name}
		  </option>
		  )) : <option disabled>Carregando serviços...</option>}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="appointment-date">Data</label>
                <DatePicker
                  id="appointment-date"
                  selected={selectedDate}
                  onChange={handleDateChange}
                  dateFormat="dd/MM/yyyy"
                  minDate={new Date()}
                  placeholderText="Selecione uma data"
                  className="form-control"
                  required
                  disabled={!selectedService}
                />
              </div>

              <div className="form-group">
                <label htmlFor="appointment-time">Horário</label>
                <select
                  id="appointment-time"
                  name="appointment-time"
                  required
                  disabled={!selectedDate || availableTimes.length === 0}
                >
                  <option value="">Selecione um horário</option>
                  {availableTimes.length > 0 ? (
                    availableTimes.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))
                  ) : (
                    <option value="" disabled>Nenhum horário disponível para esta data.</option>
                  )}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="name">Seu Nome</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Telefone (WhatsApp)</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email (Opcional)</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="notes">Observações (Opcional)</label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={3}
                  value={formData.notes}
                  onChange={handleFormChange}
                ></textarea>
              </div>
              <button type="submit" className="btn btn-primary btn-lg" disabled={status === 'loading' || !selectedService || !selectedDate || availableTimes.length === 0}>
                {status === 'loading' ? 'Agendando...' : 'Confirmar Agendamento'}
              </button>
              {responseMessage && (
                <p className={`response-message ${status}`}>{responseMessage}</p>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Appointment;

